import { useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import { TimesheetGridDialogs } from './timesheet/TimesheetGridDialogs';
import { TimesheetSubmitFooter } from './timesheet/submit-footer/TimesheetSubmitFooter';
import { TimesheetGridStickyHeader } from './timesheet/sticky-header/TimesheetGridStickyHeader';
import { TimesheetGridTable } from './timesheet/table/TimesheetGridTable';
import { useTimesheetGrid } from './timesheet/hooks/useTimesheetGrid';
import type { TimesheetDrilldownState } from './timesheet/hooks/useTimesheetGridCore';

interface TimesheetGridProps {
  selection?: TimesheetDrilldownState;
}

/**
 * Weekly timesheet grid. All state lives in `useTimesheetGrid`; this component
 * only lays out the header, grid, submit footer and dialogs.
 */
export function TimesheetGrid({ selection }: TimesheetGridProps = {}) {
  const location = useLocation();
  // Navigation state for drilldown from reports, via props or router state
  const navigationState = selection || (location.state as { timesheetDrilldown?: TimesheetDrilldownState })?.timesheetDrilldown;

  const { isReadOnly, addRow, stickyHeaderProps, tableProps, submitFooterProps, dialogProps } =
    useTimesheetGrid({ navigationState });

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full overflow-hidden">
        {/* Sticky Top Section: Page Header + Week Toolbar */}
        <TimesheetGridStickyHeader {...stickyHeaderProps} />

        {/* Scrollable content area */}
        <div className="flex-1 min-h-0 flex flex-col gap-2 sm:gap-3 pb-1">
          <TimesheetGridTable {...tableProps} />
        </div>

        {/* Submit footer - always visible at bottom */}
        <TimesheetSubmitFooter {...submitFooterProps} />

        {/* Mobile FAB (Floating Action Button) - Only visible on mobile */}
        {!isReadOnly && (
          <Button
            onClick={addRow}
            size="lg"
            className="fixed bottom-20 right-4 z-30 h-14 w-14 rounded-full shadow-2xl md:hidden hover:scale-110 transition-transform"
            title="Add Row"
          >
            <Plus className="h-6 w-6" />
          </Button>
        )}

        <TimesheetGridDialogs {...dialogProps} />
      </div>
    </TooltipProvider>
  );
}
